import { Fragment, useId, useState } from "react";
import { Badge } from "@/components/base/badges/badges";
import {
  formatEur,
  formatKc,
  type BudgetCardItem,
} from "../../schemas/budget";
import { SpzExpandableRow } from "./SpzExpandableRow";

/**
 * UC-002 SpzBudgetTable — per-SPZ Data Table s expandable row drill-down
 * (Phase 16, REQ-BIZ-007 + REQ-BIZ-008 + UX-1 drill-down 1 klik).
 *
 * Pattern: každá SPZ má dva `<tr>` — header row (toggle button + summary)
 * a inline expanded row (`<td colSpan>` s `SpzExpandableRow`). Expand
 * state je per-component (klient nepamatuje napříč navigation), což je OK
 * pro admin tooling.
 *
 * Accessibility:
 *   - `aria-expanded` na toggle button (REQ-UI-021 WCAG AA).
 *   - Expanded content má `role="region"` + aria-labelledby zpět na header label.
 *   - color-not-only — CZK + EUR + count text labels (REQ-UI-018).
 *   - Žádný expandable row pro nulový list (`Žádné SPZ.`).
 */

export interface SpzBudgetTableProps {
  items: BudgetCardItem[];
  /** Loading indicator pro <tbody>. */
  loading?: boolean;
  /** Pokud TRUE → read-only mode po approve (skrýt actions). */
  readOnly?: boolean;
}

export function SpzBudgetTable({ items, loading, readOnly }: SpzBudgetTableProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const captionId = useId();

  function toggle(id: string): void {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const th =
    "text-left px-4 py-3 border-b border-secondary font-semibold bg-secondary text-tertiary text-xs uppercase tracking-wider";
  const td = "text-left px-4 py-3 border-b border-secondary";

  return (
    <div
      className="overflow-x-auto rounded-xl bg-primary ring-1 ring-inset ring-secondary"
      data-testid="spz-budget-table"
    >
      <table
        className="w-full border-collapse text-sm text-primary"
        aria-describedby={captionId}
      >
        <caption id={captionId} className="sr-only">
          Per-SPZ rozklad rozpočtu s drill-down do transakcí.
        </caption>
        <thead>
          <tr>
            <th scope="col" aria-label="Rozbalit/sbalit" className={th}></th>
            <th scope="col" className={th}>Karta (SPZ)</th>
            <th scope="col" className={th}>Držitel</th>
            <th scope="col" className={th}>CZK</th>
            <th scope="col" className={th}>EUR</th>
            <th scope="col" className={th}>Transakce</th>
            <th scope="col" className={th}>Stav</th>
          </tr>
        </thead>
        <tbody aria-busy={loading || undefined} aria-live="polite">
          {items.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-center text-tertiary py-8">
                {loading ? "Načítám…" : "Žádné SPZ."}
              </td>
            </tr>
          ) : (
            items.map((item) => {
              const expanded = expandedIds.has(item.fuel_card_id);
              const labelId = `spz-row-${item.fuel_card_id}-label`;
              return (
                <Fragment key={item.fuel_card_id}>
                  <tr
                    className="hover:bg-secondary focus-within:outline-2 focus-within:outline-focus-ring"
                    data-testid={`spz-row-${item.fuel_card_id}`}
                    data-expanded={expanded ? "true" : "false"}
                  >
                    <td className={td}>
                      <button
                        type="button"
                        className="inline-flex items-center justify-center min-w-touch min-h-touch rounded-md border border-transparent bg-transparent text-md text-primary cursor-pointer hover:border-secondary hover:bg-secondary focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
                        aria-expanded={expanded}
                        aria-controls={`spz-expand-region-${item.fuel_card_id}`}
                        onClick={() => toggle(item.fuel_card_id)}
                        data-testid={`spz-toggle-${item.fuel_card_id}`}
                      >
                        <span aria-hidden="true">{expanded ? "▾" : "▸"}</span>
                        <span className="sr-only">
                          {expanded ? "Sbalit" : "Rozbalit"} kartu{" "}
                          {item.card_label ?? item.card_number_display ?? ""}
                        </span>
                      </button>
                    </td>
                    <td className={td}>
                      <strong id={labelId}>{item.card_label ?? "—"}</strong>
                      {item.card_number_display && (
                        <span className="block text-xs text-tertiary">
                          {item.card_number_display}
                        </span>
                      )}
                    </td>
                    <td className={td}>{item.holder_label ?? "—"}</td>
                    <td className={td} data-testid={`spz-amount-kc-${item.fuel_card_id}`}>
                      {formatKc(item.amount_kc)}
                    </td>
                    <td className={td} data-testid={`spz-amount-eur-${item.fuel_card_id}`}>
                      {formatEur(item.amount_eur)}
                    </td>
                    <td className={td}>{item.transaction_count}</td>
                    <td className={td}>
                      {item.holder_id ? (
                        <Badge color="success">Přiřazeno</Badge>
                      ) : (
                        <Badge color="warning">Bez držitele</Badge>
                      )}
                    </td>
                  </tr>
                  {expanded && (
                    <tr>
                      <td colSpan={7} className="p-0 bg-secondary/60 border-b border-secondary">
                        <div id={`spz-expand-region-${item.fuel_card_id}`}>
                          <SpzExpandableRow
                            item={item}
                            expanded={expanded}
                            labelledById={labelId}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })
          )}
        </tbody>
      </table>
      {readOnly && (
        <p className="mt-2 text-tertiary italic" data-testid="spz-table-readonly">
          Rozpočet je schválen — read-only.
        </p>
      )}
    </div>
  );
}
